// @owners { team: patients-team }
import { H1 } from '@alto/design-system';
import autobind from 'autobind-decorator';
import { format, parse } from 'date-fns';
import React from 'react';
import { push } from 'react-router-redux';
// eslint-disable-next-line import/no-deprecated
import { type ValidateUserPayload, validateUser } from '~shared/actions/auth';
// eslint-disable-next-line import/no-deprecated
import { fetchOnboardState, setInsuranceLoadingSeen } from '~shared/actions/onboarding';
import {
  ONBOARD_STATE_DONE,
  ONBOARD_STATE_MED_CONFIRMATION,
  ONBOARD_STATE_TO_ROUTE,
  type Route,
  VERIFY,
} from '~shared/features/onboarding/helpers/onboardingRouteHandler';
import getLoading from '~shared/features/ui/selectors/getLoading';
import { DATE_FORMATS } from '~shared/helpers/date';
import { sendAnalyticsEvent } from '~shared/lib/analytics/src/actions';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { createEvent } from '~shared/lib/analytics/src/helper';
import { connect } from '~shared/store';
import { type ReduxDispatchShared, type ReduxStateShared } from '~shared/types';
import { type Values as VerificationDetails, Verify, type VerifyProps } from './Verify';
import { PageBackground, SingleColumnContentWrapper } from '~web/components/Page';
import { nextUrl } from '~web/features/onboarding/helpers/urlHandler';

type Props = VerifyProps & {
  readonly fetchOnboardState: (onboardingState: string) => Promise<{
    onboard_state?: string;
    error?: Error;
  }>;
  readonly validateUser: (userPayload: ValidateUserPayload) => Promise<boolean>;
  readonly setInsuranceLoadingSeen: typeof setInsuranceLoadingSeen;
  readonly nextRoute: (routeName?: Route) => string;
  readonly push: (url: string) => void;
  readonly trackOnboardingVerifyViewed: (tempPassword?: string) => void;
};

type State = {
  onboardState: string | null | undefined;
};

class OnboardingVerifyContainer extends React.PureComponent<Props, State> {
  state = {
    onboardState: null,
  };

  componentDidMount() {
    const { fetchOnboardState, push, tempPassword, trackOnboardingVerifyViewed } = this.props;
    // eslint-disable-next-line promise/catch-or-return
    fetchOnboardState(tempPassword ?? '').then((result) => {
      const onboardState = result.onboard_state;
      this.setState({
        onboardState,
      });

      // eslint-disable-next-line promise/always-return
      if (onboardState === ONBOARD_STATE_DONE) {
        push('/');
      }
    });

    trackOnboardingVerifyViewed(tempPassword);
  }

  // @ts-expect-error error TS1270: Decorator function return type 'void | TypedPropertyDescriptor<unknown>' is not assignable to type 'void | (() => void)'
  @autobind
  onVerify(data: VerificationDetails) {
    const { tempPassword } = this.props;
    const dob = format(
      parse(data.date_of_birth, DATE_FORMATS.DATE_INPUT_FORMAT, 0),
      DATE_FORMATS.YEAR_MONTH_DAY_DASHED,
    );
    // eslint-disable-next-line promise/catch-or-return
    this.props
      .validateUser({
        last_name: data.last_name,
        date_of_birth: dob,
        token: tempPassword ?? '',
        terms: data.terms,
      })
      .then((success) => {
        if (!success) {
          return;
        }

        const { onboardState } = this.state;

        // eslint-disable-next-line promise/always-return
        if (onboardState) {
          if (this.isAfterInsuranceLoading(onboardState)) {
            this.props.setInsuranceLoadingSeen();
          }

          this.props.push(this.props.nextRoute(ONBOARD_STATE_TO_ROUTE[onboardState]));
        } else {
          this.props.push(this.props.nextRoute());
        }
      });
  }

  isAfterInsuranceLoading(onboardState: string) {
    return onboardState !== ONBOARD_STATE_MED_CONFIRMATION;
  }

  render() {
    return (
      <PageBackground white>
        <SingleColumnContentWrapper>
          <H1 center>Let’s verify your identity.</H1>
          <Verify
            {...this.props}
            // eslint-disable-next-line @typescript-eslint/unbound-method
            onVerify={this.onVerify}
          />
        </SingleColumnContentWrapper>
      </PageBackground>
    );
  }
}

const mapStateToProps = (state: ReduxStateShared, ownProps: { params?: { tempPassword: string } }) => {
  const { validatingUserLoading } = getLoading(state);

  return {
    verifyLoading: validatingUserLoading,
    nextRoute: (desiredOnboardRoute?: Route) => nextUrl(state, VERIFY, desiredOnboardRoute),
    tempPassword: ownProps.params?.tempPassword,
  };
};

const mapDispatchToProps = (dispatch: ReduxDispatchShared) => ({
  // eslint-disable-next-line import/no-deprecated
  fetchOnboardState: (tempPassword: string) => dispatch(fetchOnboardState(tempPassword)),
  // eslint-disable-next-line import/no-deprecated
  validateUser: (userData: ValidateUserPayload) => dispatch(validateUser(userData)),
  setInsuranceLoadingSeen: () => dispatch(setInsuranceLoadingSeen()),
  push: (routeName: string) => dispatch(push(routeName)),
  trackOnboardingVerifyViewed: (tempPassword?: string) => {
    dispatch(
      sendAnalyticsEvent(
        createEvent(
          EVENTS.ONBOARDING__VERIFY_ACCOUNT_VIEWED,
          {
            flow: 'onboard',
          },
          {
            userIdentifier: tempPassword ?? '',
            userIdentifierType: 'temp_password',
          },
        ),
      ),
    );
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(OnboardingVerifyContainer);
